/**
 * The Home 3D live-model rule (card t_4e78c7af) — what "the hub served a newer
 * revision" means, and when the open page may act on it.
 *
 * The hub rebuilds and re-serves the demo house whenever device states change
 * (`otto-design/DemoHouse/house-ifc/tools/state_sync.py --watch`), and every
 * re-serve REPLACES the dashboard's model row — the upload route deletes the
 * previous row and creates a new one — so a revision's identity is its row
 * `id`. `updatedAt`/`fileSize` tell the same story twice (a rebuild from a moved
 * shade is a different number of bytes) and are read as well, because the
 * comparison must not depend on which of the three the hub happens to stamp.
 *
 * Measured before this module existed: the served revision changed every
 * ~1-2 min while the open page kept painting the first one — the owner only saw
 * new states after a manual reload ("still nothing..").
 *
 * Pure on purpose: the poll's decision is a unit test
 * (`bimModelRefresh.test.ts`), so this module stays importable from a plain
 * Node test — no stores, no Vue, no three.
 */
import { field, wireArray, wireNumber, wireString } from './wire';

/**
 * How often the open page asks the hub which revision it serves. The card's own
 * cadence (15–20 s): shorter than the hub's rebuild loop (~1-2 min), long
 * enough that an idle page is not a poller worth noticing.
 */
export const BIM_MODEL_POLL_MS = 15_000;

/**
 * The model row's own id, or `null` when the payload names none.
 *
 * The `id` field is the row's identity; the file route spells the same id as
 * `…/bim-models/<modelId>/file`, which is the fallback for a payload whose `id`
 * did not survive the wire.
 */
export function bimModelIdOf(row: unknown): string | null {
  const id = wireString(field(row, 'id'));
  if (id !== undefined && id.length > 0) return id;
  const fileApiPath = wireString(field(row, 'fileApiPath'));
  if (fileApiPath === undefined) return null;
  const match = /\/bim-models\/([^/]+)\/file\/?$/.exec(fileApiPath);
  return match ? match[1] : null;
}

/**
 * A revision's signature: the id its row carries, plus the two stamps that say
 * "this file is not that file" when a hub reuses a row. Two reads of the same
 * revision are equal; anything the hub re-served is not.
 */
export function bimModelSignature(row: unknown): string | null {
  const id = bimModelIdOf(row);
  if (id === null) return null;
  const stamp = wireString(field(row, 'updatedAt')) ?? wireString(field(row, 'createdAt')) ?? '';
  const size = wireNumber(field(row, 'fileSize'));
  return `${id}|${stamp}|${size ?? ''}`;
}

/**
 * A revision's time, for picking the newest of several rows. `updatedAt` wins
 * over `createdAt`; an ISO-8601 string is comparable as a string. A row that
 * names no time reads as `null` and cannot be placed on the timeline.
 */
function revisionStamp(row: unknown): string | null {
  return wireString(field(row, 'updatedAt')) ?? wireString(field(row, 'createdAt')) ?? null;
}

/**
 * The newest model row of a `GET /dashboards/:id/bim-models` answer.
 *
 * The hub lists rows `createdAt` ASC (BimRepository), so the last row is the
 * newest — and that is also the fallback for a payload that carries no
 * timestamps at all. When some rows do carry one, the greatest stamp wins: a row
 * that names no time cannot be compared, and the app's own upload always stamps
 * `createdAt`. Rows without an id are dropped — nothing can be fetched or
 * compared for them.
 */
export function newestBimModelRow<T>(rows: readonly T[] | undefined | null): T | null {
  const list = (wireArray(rows) ?? []).filter((row): row is T => bimModelIdOf(row) !== null);
  if (list.length === 0) return null;
  let best = list[list.length - 1];
  let bestStamp = revisionStamp(best);
  for (const row of list.slice(0, -1)) {
    const stamp = revisionStamp(row);
    if (stamp !== null && (bestStamp === null || stamp > bestStamp)) {
      best = row;
      bestStamp = stamp;
    }
  }
  return best;
}

/**
 * The URL the model's bytes are fetched from.
 *
 * The stable `/api` file route wins over the presigned MinIO URL: it works on
 * every host (Electron cloud mode goes through the WebRTC proxy, a hub-served
 * page is same-origin), which is what the app's loader was changed for.
 */
export function bimModelFileUrl(row: unknown): string | null {
  const fileApiPath = wireString(field(row, 'fileApiPath'));
  if (fileApiPath !== undefined && fileApiPath.length > 0) return fileApiPath;
  const modelUrl = wireString(field(row, 'modelUrl'));
  if (modelUrl !== undefined && modelUrl.length > 0) return modelUrl;
  return null;
}

/** The two facts the poll's decision needs, plus the pause it must respect. */
export interface BimSwapGate {
  /** Signature of the revision ON SCREEN; `null` = no revision loaded (and no swap). */
  loaded: string | null;
  /** Signature of the newest revision the hub just answered with. */
  served: string | null;
  /**
   * Something must not be disturbed: the page is not the surface the operator is
   * looking at, an edit is in flight (a device being placed, an element drag, an
   * unsaved form, a PUT on the wire), or a swap is already running.
   */
  paused: boolean;
}

/**
 * Whether to swap the scene's model for the served one. A swap needs a loaded
 * revision to replace AND a different one to replace it with — an empty answer
 * (the hub is between the delete and the create of a re-serve) is not a revision
 * and never unloads what is on screen.
 */
export function shouldSwapBimModel(gate: BimSwapGate): boolean {
  if (gate.paused) return false;
  if (gate.loaded === null || gate.served === null) return false;
  return gate.loaded !== gate.served;
}

/** A camera pose, as the swap reports it (position + orbit target). */
export interface BimCameraPoseLike {
  position: [number, number, number];
  target: [number, number, number];
}

/**
 * Whether two poses are the same view. Used as the swap's own proof: the live
 * swap must leave the camera exactly where the user put it, and the log line
 * says so from measured numbers rather than from intent.
 */
export function bimPosesMatch(
  before: BimCameraPoseLike | null,
  after: BimCameraPoseLike | null,
  epsilon = 1e-3,
): boolean {
  if (!before || !after) return false;
  const same = (a: [number, number, number], b: [number, number, number]) =>
    a.every((value, index) => Math.abs(value - b[index]) <= epsilon);
  return same(before.position, after.position) && same(before.target, after.target);
}

/**
 * The row a REFUSED write should be re-pointed at: the newest row the hub
 * serves, when it is NOT the row the failed write used. `null` = nothing newer.
 *
 * Why the write path needs it: the re-serve replaces the dashboard's model row
 * every ~1-2 min (`id` IS the revision), and an editing page keeps its live poll
 * paused — `liveEditInFlight` refuses to re-seat the model under a selected
 * element — so the id the page writes to is often already gone by the time the
 * user clicks Remove: the hub answers `404 Model not found` and the edit is
 * rolled back on screen. The hub carries the document onto the new row, so the
 * SAME document belongs to it; this says which row that is. A `null` (same id,
 * no rows, the delete-then-create window) means the refusal was about the write
 * itself and must not be retried anywhere else.
 */
export function newerRowIdFor(attemptedId: string | null, rows: readonly unknown[] | null | undefined): string | null {
  const newest = newestBimModelRow(rows);
  const id = newest === null ? null : bimModelIdOf(newest);
  return id === null || id === attemptedId ? null : id;
}
